import type { GeneralsReplay, Owner } from "./generals-board-model";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
function isArray(value: unknown): value is readonly unknown[] {
  return Array.isArray(value);
}
function isOwner(owner: unknown): owner is Owner {
  return owner === -1 || owner === 0 || owner === 1;
}
function isArmy(army: unknown): army is number {
  return typeof army === "number" && Number.isInteger(army) && army >= 0;
}
function isDimension(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}
function isFirstFrame({
  value,
  tileCount,
}: {
  readonly value: unknown;
  readonly tileCount: number;
}) {
  if (!isRecord(value) || !isArray(value.o) || !isArray(value.a)) return false;

  return (
    value.o.length === tileCount &&
    value.a.length === tileCount &&
    value.o.every(isOwner) &&
    value.a.every(isArmy)
  );
}
function isReplayFrame({
  frame,
  isIndex,
}: {
  readonly frame: unknown;
  readonly isIndex: (value: unknown) => boolean;
}) {
  if (!isArray(frame) || frame.length % 3 !== 0) return false;

  return frame.every((entry, index) => {
    if (index % 3 === 0) return isIndex(entry);

    if (index % 3 === 1) return isOwner(entry);

    return isArmy(entry);
  });
}
function hasReplayTerrain({
  replay,
  tileCount,
}: {
  readonly replay: Record<string, unknown>;
  readonly tileCount: number;
}) {
  const isIndex = (index: unknown) => isArmy(index) && index < tileCount;

  if (
    !isArray(replay.mountains) ||
    !isArray(replay.generals) ||
    !isArray(replay.cities) ||
    !isArray(replay.frames)
  )
    return false;

  return (
    replay.mountains.every(isIndex) &&
    replay.generals.every(isIndex) &&
    replay.cities.every(
      (city) =>
        isArray(city) &&
        city.length === 2 &&
        isIndex(city[0]) &&
        isArmy(city[1]),
    ) &&
    replay.frames.every((frame) => isReplayFrame({ frame, isIndex }))
  );
}

export function isReplay(value: unknown): value is GeneralsReplay {
  if (
    !isRecord(value) ||
    value.v !== 1 ||
    !isDimension(value.rows) ||
    !isDimension(value.cols)
  )
    return false;

  const tileCount = value.rows * value.cols;
  return (
    isFirstFrame({ value: value.first, tileCount }) &&
    hasReplayTerrain({ replay: value, tileCount })
  );
}
